import React, { useState, useEffect } from "react";
import { X, Info, Clock, Zap } from "lucide-react";
import Button from "./Button";
import { noticeStorage } from "../../utils/noticeStorage";
import { modalSettingsService } from "../../lib/modalSettingsService";

/**
 * NoticeModal - Displays important announcements to affiliates
 *
 * Features:
 * - Shows automatically on affiliate login/visit
 * - Remembers when last shown using localStorage
 * - Only displays every 6 hours to avoid annoying users
 * - Beautiful glass morphism design with animations
 * - Debug functions available via window.noticeDebug
 *
 * Testing:
 * - Use window.noticeDebug.reset() to clear storage and test modal
 * - Use window.noticeDebug.getDebugInfo() to see current status
 */
const NoticeModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [modalSettings, setModalSettings] = useState(null);

  useEffect(() => {
    const checkAndShowModal = async () => {
      try {
        // First check if modal is enabled in admin settings
        console.log("📢 Notice Modal: Checking admin settings...");
        const settings = await modalSettingsService.getClientSettings();
        setModalSettings(settings);

        if (!settings.notice_modal_enabled) {
          console.log("📢 Notice Modal: Disabled by admin - not showing");
          return;
        }

        // Update the show interval based on admin settings
        const customInterval =
          settings.notice_modal_show_interval_hours * 60 * 60 * 1000;

        // Check if we should show based on custom interval
        const lastShown = localStorage.getItem("affiliate_notice_last_shown");
        const now = Date.now();

        if (!lastShown) {
          // First time - show modal
          console.log("📢 Notice Modal: First time - scheduling display");
          scheduleModalDisplay();
          return;
        }

        const lastShownTime = parseInt(lastShown, 10);
        const timeSinceLastShown = now - lastShownTime;

        if (timeSinceLastShown >= customInterval) {
          console.log(
            "📢 Notice Modal: Time interval passed - scheduling display"
          );
          scheduleModalDisplay();
        } else {
          console.log("📢 Notice Modal: Not showing - shown recently");
          console.log("📢 Debug info:", {
            lastShown: new Date(lastShownTime).toLocaleString(),
            intervalHours: settings.notice_modal_show_interval_hours,
            timeUntilNext:
              Math.ceil(
                (customInterval - timeSinceLastShown) / (60 * 60 * 1000)
              ) + " hours",
          });
        }
      } catch (error) {
        console.error("❌ Error checking modal settings:", error);
        console.log("🔄 Falling back to default behavior due to error");
        // Fallback to default behavior - always show for testing
        scheduleModalDisplay();
      }
    };

    const scheduleModalDisplay = () => {
      // Small delay to ensure page is loaded
      const timer = setTimeout(() => {
        console.log("📢 Notice Modal: Displaying modal now");
        setIsVisible(true);
      }, 1000);

      return () => clearTimeout(timer);
    };

    checkAndShowModal();
  }, []);

  const handleClose = () => {
    console.log("📢 Notice Modal: User closed modal - marking as shown");
    // Update storage with current timestamp
    noticeStorage.markAsShown();
    setIsVisible(false);
    console.log("📢 Debug info after close:", noticeStorage.getDebugInfo());
  };

  if (!isVisible || !modalSettings) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/60 p-4">
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/40 max-w-lg w-full relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#B8460E]/10 rounded-full blur-3xl animate-pulse"></div>
          <div
            className="absolute bottom-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] animate-shine pointer-events-none rounded-2xl"></div>

        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 z-20"
          onClick={handleClose}
        >
          <X className="h-6 w-6" />
        </button>

        {/* Content */}
        <div className="relative z-10 p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-[#B8460E]/20 rounded-xl">
              <Info className="h-6 w-6 text-[#B8460E]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">
                {modalSettings.notice_modal_title}
              </h2>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Clock className="h-4 w-4" />
                <span>{modalSettings.notice_modal_date}</span>
              </div>
            </div>
          </div>

          {/* Message content */}
          <div className="space-y-4 mb-8">
            {modalSettings.notice_modal_content?.intro && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <p className="text-gray-300 leading-relaxed">
                  {modalSettings.notice_modal_content.intro}
                </p>
              </div>
            )}

            {modalSettings.notice_modal_content?.main && (
              <div className="bg-gradient-to-r from-[#B8460E]/10 to-blue-500/10 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-[#B8460E]/20 rounded-lg mt-1">
                    <Zap className="h-5 w-5 text-[#B8460E]" />
                  </div>
                  <div>
                    <p className="text-gray-300 leading-relaxed">
                      {modalSettings.notice_modal_content.main}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {modalSettings.notice_modal_content?.closing && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <p className="text-gray-300 leading-relaxed">
                  {modalSettings.notice_modal_content.closing}
                </p>
              </div>
            )}

            {/* Fallback content if no database content */}
            {!modalSettings.notice_modal_content?.intro &&
              !modalSettings.notice_modal_content?.main &&
              !modalSettings.notice_modal_content?.closing && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                  <p className="text-red-300">
                    No content loaded from database. Using fallback message.
                  </p>
                  <p className="text-gray-300 leading-relaxed mt-2">
                    This is a test modal. Please configure content in Admin
                    Settings → Modal Settings.
                  </p>
                </div>
              )}
          </div>

          {/* Action button */}
          <div className="flex justify-center">
            <Button
              onClick={handleClose}
              className="bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-3 text-lg font-semibold"
            >
              Got it, thanks! 🚀
            </Button>
          </div>

          {/* Footer note */}
          <div className="text-center mt-6">
            <p className="text-xs text-gray-500">
              This notice will show again in{" "}
              {modalSettings.notice_modal_show_interval_hours} hours if there
              are updates
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeModal;
